import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { ContractForWorkService } from "services/contract-for-work";
import { getPrintTemplate } from "./components/printTemplate";

const splitDate = (text = "") => {
  const [day = "", month = "", year = ""] = String(text).split("/");
  return { day: day.replace(/\D/g, "").slice(0, 2), month: month.replace(/\D/g, "").slice(0, 2), year: year.replace(/\D/g, "").slice(0, 4) };
};

const composeDate = (parts) => {
  if (!parts?.day || !parts?.month || !parts?.year) return "";
  return `${String(parts.day).padStart(2, "0")}/${String(parts.month).padStart(2, "0")}/${parts.year}`;
};

const createLine = () => "";

export default function ContractForWorkDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [dateParts, setDateParts] = useState({ day: "", month: "", year: "" });
  const [form, setForm] = useState({
    unit: "",
    department: "",
    contractNumber: "",
    assignorName: "",
    assignorTitle: "",
    assignorRepresentativeOf: "",
    assigneeName: "",
    assigneeTitle: "",
    assigneeRepresentativeOf: "",
    assignmentMethod: "",
    executionConditions: "",
    executionDuration: "",
    otherConditions: "",
    workContents: [createLine()],
    assigneeResponsibilities: [createLine()],
    assignorResponsibilities: [createLine()],
  });

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await ContractForWorkService.getById(id);
        const data = response.data ? response.data : response;

        setForm({
          unit: data.unit || "",
          department: data.department || "",
          contractNumber: data.contractNumber || "",
          assignorName: data.assignorName || "",
          assignorTitle: data.assignorTitle || "",
          assignorRepresentativeOf: data.assignorRepresentativeOf || "",
          assigneeName: data.assigneeName || "",
          assigneeTitle: data.assigneeTitle || "",
          assigneeRepresentativeOf: data.assigneeRepresentativeOf || "",
          assignmentMethod: data.assignmentMethod || "",
          executionConditions: data.executionConditions || "",
          executionDuration: data.executionDuration || "",
          otherConditions: data.otherConditions || "",
          workContents: data.workContents?.length ? data.workContents : [createLine()],
          assigneeResponsibilities: data.assigneeResponsibilities?.length ? data.assigneeResponsibilities : [createLine()],
          assignorResponsibilities: data.assignorResponsibilities?.length ? data.assignorResponsibilities : [createLine()],
        });

        setDateParts(splitDate(data.contractDate || ""));
      } catch (error) {
        toast.error("Không thể tải dữ liệu hợp đồng");
      }
    })();
  }, [id]);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const setDateField = (field, value) => {
    const max = field === "year" ? 4 : 2;
    const raw = value.replace(/\D/g, "");
    setDateParts((prev) => ({ ...prev, [field]: field === "year" ? raw.slice(0, max) : raw.slice(-max) }));
  };

  const padDatePartOnBlur = (field) => {
    if (field === "year") return;
    setDateParts((prev) => {
      const value = String(prev[field] || "");
      if (!value) return prev;
      return { ...prev, [field]: value.padStart(2, "0") };
    });
  };

  const setLineField = (group, index, value) => {
    setForm((prev) => {
      const next = [...prev[group]];
      next[index] = value;
      return { ...prev, [group]: next };
    });
  };

  const addLine = (group) => {
    setForm((prev) => ({ ...prev, [group]: [...prev[group], createLine()] }));
  };

  const removeLine = (group, index) => {
    setForm((prev) => {
      const next = prev[group].filter((_, i) => i !== index);
      return { ...prev, [group]: next.length ? next : [createLine()] };
    });
  };

  const normalizeLines = (values = []) => values.map((item) => String(item || "").trim()).filter(Boolean);

  const buildPayload = () => {
    const contractDate = composeDate(dateParts);
    if (!contractDate) throw new Error("Ngày hợp đồng chưa hợp lệ");

    const required = [
      ["unit", "Đơn vị"],
      ["department", "Bộ phận"],
      ["contractNumber", "Số hợp đồng"],
      ["assignorName", "Họ tên bên giao khoán"],
      ["assignorTitle", "Chức vụ bên giao khoán"],
      ["assignorRepresentativeOf", "Đại diện bên giao khoán"],
      ["assigneeName", "Họ tên bên nhận khoán"],
      ["assigneeTitle", "Chức vụ bên nhận khoán"],
      ["assigneeRepresentativeOf", "Đại diện bên nhận khoán"],
    ];

    for (const [field, label] of required) {
      if (!String(form[field] || "").trim()) throw new Error(`${label} không được để trống`);
    }

    const workContents = normalizeLines(form.workContents);
    const assigneeResponsibilities = normalizeLines(form.assigneeResponsibilities);
    const assignorResponsibilities = normalizeLines(form.assignorResponsibilities);

    if (!workContents.length) throw new Error("Cần nhập ít nhất 1 dòng nội dung công việc khoán");
    if (!assigneeResponsibilities.length) throw new Error("Cần nhập ít nhất 1 dòng trách nhiệm của người nhận khoán");
    if (!assignorResponsibilities.length) throw new Error("Cần nhập ít nhất 1 dòng trách nhiệm của bên giao khoán");

    return {
      ...form,
      contractDate,
      workContents,
      assigneeResponsibilities,
      assignorResponsibilities,
    };
  };

  const handleSubmit = async () => {
    try {
      setLoadingSubmit(true);
      const payload = buildPayload();
      if (id) {
        await ContractForWorkService.update(id, payload);
        toast.success("Cập nhật hợp đồng thành công");
      } else {
        await ContractForWorkService.create(payload);
        toast.success("Tạo hợp đồng thành công");
      }
      navigation("/templates/contract-for-work");
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || "Không thể lưu hợp đồng giao khoán");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handlePrint = () => {
    const htmlContent = getPrintTemplate({ data: { ...form, contractDate: composeDate(dateParts) } });
    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const renderLineGroup = (title, group) => (
    <SoftBox mt={2}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{title}</div>
      {(form[group] || []).map((line, index) => (
        <div key={`${group}-${index}`} style={{ display: "flex", alignItems: "center", marginBottom: 4, gap: 6 }}>
          <span>-</span>
          {containView ? (
            <span>{line || ""}</span>
          ) : (
            <>
              <input
                style={{ width: "100%", border: "none", borderBottom: "1px dotted #000", outline: "none", fontSize: 14, fontFamily: "inherit" }}
                value={line}
                onChange={(e) => setLineField(group, index, e.target.value)}
              />
              <button type="button" onClick={() => removeLine(group, index)} style={{ border: "none", background: "transparent", color: "red" }}>
                X
              </button>
            </>
          )}
        </div>
      ))}
      {!containView && (
        <SoftButton variant="outlined" color="dark" size="small" onClick={() => addLine(group)}>
          THÊM DÒNG
        </SoftButton>
      )}
    </SoftBox>
  );

  const inputStyle = { border: "none", borderBottom: "1px dotted #000", outline: "none", fontSize: 14, fontFamily: "inherit", background: "transparent" };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={20}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Card>
              <SoftBox p={2} style={{ fontFamily: '"Times New Roman", Times, serif', fontSize: 16, color: "#000", backgroundColor: "#fff", padding: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                  <div>
                    <div><b>Đơn vị:</b> <input disabled={containView} style={{ ...inputStyle, width: 220 }} value={form.unit} onChange={(e) => setField("unit", e.target.value)} /></div>
                    <div><b>Bộ phận:</b> <input disabled={containView} style={{ ...inputStyle, width: 220 }} value={form.department} onChange={(e) => setField("department", e.target.value)} /></div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div><b>Mẫu số: 05 - LĐTL</b></div>
                    <div style={{ fontStyle: "italic", fontSize: 13 }}>(Kèm theo Thông tư số 99/2025/TT-BTC<br />ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</div>
                  </div>
                </div>

                <h2 style={{ textAlign: "center", margin: "10px 0 4px", fontWeight: 700 }}>HỢP ĐỒNG GIAO KHOÁN</h2>
                <div style={{ textAlign: "center", marginBottom: 8 }}>
                  Ngày
                  <input disabled={containView} style={{ ...inputStyle, width: 34, textAlign: "center", margin: "0 4px" }} value={dateParts.day} onChange={(e) => setDateField("day", e.target.value)} onBlur={() => padDatePartOnBlur("day")} />
                  tháng
                  <input disabled={containView} style={{ ...inputStyle, width: 34, textAlign: "center", margin: "0 4px" }} value={dateParts.month} onChange={(e) => setDateField("month", e.target.value)} onBlur={() => padDatePartOnBlur("month")} />
                  năm
                  <input disabled={containView} style={{ ...inputStyle, width: 50, textAlign: "center", margin: "0 4px" }} value={dateParts.year} onChange={(e) => setDateField("year", e.target.value)} />
                </div>

                <div style={{ textAlign: "right", marginBottom: 8 }}>
                  Số: <input disabled={containView} style={{ ...inputStyle, width: 140 }} value={form.contractNumber} onChange={(e) => setField("contractNumber", e.target.value)} />
                </div>

                <div style={{ marginBottom: 8 }}>
                  Họ và tên: <input disabled={containView} style={{ ...inputStyle, width: 260 }} value={form.assignorName} onChange={(e) => setField("assignorName", e.target.value)} />
                  &nbsp; Chức vụ: <input disabled={containView} style={{ ...inputStyle, width: 180 }} value={form.assignorTitle} onChange={(e) => setField("assignorTitle", e.target.value)} />
                </div>
                <div style={{ marginBottom: 8 }}>
                  Đại diện <input disabled={containView} style={{ ...inputStyle, width: 280 }} value={form.assignorRepresentativeOf} onChange={(e) => setField("assignorRepresentativeOf", e.target.value)} /> bên giao khoán
                </div>

                <div style={{ marginBottom: 8 }}>
                  Họ và tên: <input disabled={containView} style={{ ...inputStyle, width: 260 }} value={form.assigneeName} onChange={(e) => setField("assigneeName", e.target.value)} />
                  &nbsp; Chức vụ: <input disabled={containView} style={{ ...inputStyle, width: 180 }} value={form.assigneeTitle} onChange={(e) => setField("assigneeTitle", e.target.value)} />
                </div>
                <div style={{ marginBottom: 8 }}>
                  Đại diện <input disabled={containView} style={{ ...inputStyle, width: 280 }} value={form.assigneeRepresentativeOf} onChange={(e) => setField("assigneeRepresentativeOf", e.target.value)} /> bên nhận khoán
                </div>

                <div style={{ fontWeight: 700, marginBottom: 8 }}>CÙNG KÝ KẾT HỢP ĐỒNG GIAO KHOÁN NHƯ SAU:</div>

                <div style={{ fontWeight: 700 }}>I- Điều khoản chung:</div>
                <div style={{ marginTop: 6 }}>- Phương thức giao khoán: <input disabled={containView} style={{ ...inputStyle, width: "65%" }} value={form.assignmentMethod} onChange={(e) => setField("assignmentMethod", e.target.value)} /></div>
                <div style={{ marginTop: 6 }}>- Điều kiện thực hiện hợp đồng: <input disabled={containView} style={{ ...inputStyle, width: "60%" }} value={form.executionConditions} onChange={(e) => setField("executionConditions", e.target.value)} /></div>
                <div style={{ marginTop: 6 }}>- Thời gian thực hiện hợp đồng: <input disabled={containView} style={{ ...inputStyle, width: "59%" }} value={form.executionDuration} onChange={(e) => setField("executionDuration", e.target.value)} /></div>
                <div style={{ marginTop: 6 }}>- Các điều kiện khác: <input disabled={containView} style={{ ...inputStyle, width: "66%" }} value={form.otherConditions} onChange={(e) => setField("otherConditions", e.target.value)} /></div>

                <SoftBox mt={2}>
                  <div style={{ fontWeight: 700 }}>II- Điều khoản cụ thể:</div>
                </SoftBox>

                {renderLineGroup("1. Nội dung công việc khoán:", "workContents")}
                {renderLineGroup("2. Trách nhiệm, quyền lợi và nghĩa vụ của người nhận khoán:", "assigneeResponsibilities")}
                {renderLineGroup("3. Trách nhiệm, quyền lợi và nghĩa vụ của bên giao khoán:", "assignorResponsibilities")}

                <SoftBox mt={3} display="flex" justifyContent="space-between">
                  <SoftButton variant="gradient" color="light" onClick={() => navigation(-1)}>Trở về</SoftButton>
                  <SoftBox display="flex" gap={2}>
                    {containView ? (
                      <SoftButton variant="gradient" color="success" onClick={handlePrint}>In hợp đồng</SoftButton>
                    ) : (
                      <SoftButton variant="gradient" color="dark" onClick={handleSubmit} disabled={loadingSubmit}>
                        {loadingSubmit ? "Đang lưu..." : id ? "Lưu" : "Tạo"}
                      </SoftButton>
                    )}
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}
